/**
 * Schedule visibility for one student — the one rule the backend and mobile share.
 *
 * The backend schedule gate and the mobile `useIsScheduleHidden` hook both ask
 * "is this person's schedule hidden by the school's schedule grade list"
 * (SCH-2501). They call this function so the two answers cannot disagree.
 *
 * A schedule is hidden only when all of these hold:
 *   - the school's `globalFeatures.schedule.studentGradeLevels` list is set;
 *   - the person is a student and not faculty, teacher or staff;
 *   - the person's current grade is a string that is not on the list.
 * A null or missing grade means shown. An empty list hides every student who
 * has a grade on record.
 */

/** The raw per-school role booleans the rule reads. */
export interface ScheduleVisibilityRoles {
  isStudent?: boolean
  isFaculty?: boolean
  isTeacher?: boolean
  isStaff?: boolean
}

/** The facts about the person whose schedule it is, from their membership at the school. */
export interface ScheduleVisibilityPerson {
  roles: ScheduleVisibilityRoles | null | undefined
  /** The current grade's public ID; `null` or missing means no grade on record (shown). */
  gradeLevelPublicId?: string | null
}

/** The schedule block, widened so a parsed block or a sparse read both fit. */
export type ScheduleVisibilityConfig = { studentGradeLevels?: readonly string[] }

/** A person holding any of these is never hidden, even if also a student. */
const STAFF_ROLE_FLAGS = ['isFaculty', 'isTeacher', 'isStaff'] as const

/** Whether the school's schedule grade list hides this person's schedule. */
export function isScheduleHiddenForStudent(
  config: ScheduleVisibilityConfig | null | undefined,
  person: ScheduleVisibilityPerson
): boolean {
  const list = config?.studentGradeLevels
  if (!list) return false
  const roles = person.roles
  if (roles?.isStudent !== true) return false
  if (STAFF_ROLE_FLAGS.some(flag => roles[flag] === true)) return false
  const grade = person.gradeLevelPublicId
  if (typeof grade !== 'string') return false
  return !list.includes(grade)
}
